import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { passwordField } from "@themislex/shared";
import { useAuth } from "../auth/AuthContext";
import { Emblem } from "../components/Emblem";
import { Button, ErrorNote, Input } from "../components/ui";
import { useTheme } from "../theme/ThemeContext";

/**
 * One-time client account activation. The lawyer shares this link manually;
 * the token is single use and expires after 14 days.
 */
export default function Setup() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { completeSetup } = useAuth();
  const { firmName } = useTheme();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);

    const parsed = passwordField.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Choose a stronger password.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      const user = await completeSetup(token, password);
      navigate(user.role === "lawyer" ? "/desk" : "/portal", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Activation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <main className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <Emblem size={64} className="text-ink" />
          <h1 className="mt-4 font-heading text-2xl font-bold">{firmName}</h1>
          <p className="mt-1 text-xs font-medium uppercase tracking-[0.28em] text-muted">
            Activate your account
          </p>
        </div>

        <form className="card p-6" onSubmit={submit} noValidate>
          {token ? (
            <>
              <p className="mb-4 text-sm text-muted">
                Choose a password for your client portal account. You will use it
                with the email address the chambers has on record for you.
              </p>
              <div className="space-y-4">
                <Input
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={10}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="At least 10 characters, including a letter and a digit."
                />
                <Input
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              {error && (
                <div className="mt-4">
                  <ErrorNote>{error}</ErrorNote>
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                className="mt-6 w-full"
              >
                {busy ? "Setting up…" : "Activate account"}
              </Button>
            </>
          ) : (
            <ErrorNote>
              This page needs a valid setup link. Please ask the chambers for a new
              link.
            </ErrorNote>
          )}
        </form>
      </main>
    </div>
  );
}
